/**
 * Validation Service
 * Handles all API-based validations for user records
 */
import { normalizePhoneNumber, isValidPhoneNumber } from "../utils/phoneNormalizer";
/**
 * Create a new validation service instance
 */
export function createValidationService(get, put) {
    const cache = {
        credentials: null,
        specialties: null,
        authenticators: null,
        pagerCarriers: null,
    };
    /**
     * Fetch reference data with caching
     */
    async function fetchReferenceData(endpoint, cacheKey) {
        // Check cache first
        const cached = cache[cacheKey];
        if (cached !== null) {
            return cached;
        }
        try {
            const response = await get(endpoint);
            const data = response.data;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            cache[cacheKey] = data;
            return data;
        }
        catch (error) {
            console.warn(`Failed to fetch ${endpoint}:`, error);
            return null;
        }
    }
    /**
     * Validate required fields
     */
    function validateRequiredFields(record) {
        const requiredFields = [
            { attr: "lastName", display: "Last Name" },
            { attr: "firstName", display: "First Name" },
            { attr: "email", display: "Email Address" },
            { attr: "copyUserId", display: "Reference V5UserId" },
        ];
        for (const { attr, display } of requiredFields) {
            const value = record[attr];
            if (!value) {
                record.validationErrors.push({
                    rowNumber: record.rowNumber,
                    fieldName: display,
                    errorMessage: "Required field is missing or empty",
                });
            }
        }
    }
    /**
     * Validate NPI format (10 digits)
     */
    function validateNPI(record) {
        if (record.npi) {
            if (!/^\d{10}$/.test(record.npi)) {
                record.validationErrors.push({
                    rowNumber: record.rowNumber,
                    fieldName: "NPI",
                    errorMessage: "Must be exactly 10 digits",
                });
            }
        }
    }
    /**
     * Validate and normalize phone number
     */
    function validatePhoneNumber(record, fieldAttr, fieldDisplay) {
        const value = record[fieldAttr];
        if (value) {
            const normalized = normalizePhoneNumber(value);
            if (!normalized || !isValidPhoneNumber(normalized)) {
                record.validationErrors.push({
                    rowNumber: record.rowNumber,
                    fieldName: fieldDisplay,
                    errorMessage: "Must be 10 digits",
                });
            }
            else {
                // Store normalized version back
                record[fieldAttr] = normalized;
            }
        }
    }
    /**
     * Validate format for Reference V5UserId / Copy User ID
     */
    function validateReferenceUserIdFormat(record) {
        if (record.copyUserId) {
            const guidPattern = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
            const v5UserIdPattern = /^USER-[A-Z0-9]+$/;
            if (!guidPattern.test(record.copyUserId) && !v5UserIdPattern.test(record.copyUserId)) {
                record.validationErrors.push({
                    rowNumber: record.rowNumber,
                    fieldName: "Reference V5UserId",
                    errorMessage: "Must be in V5UserId format (e.g., USER-8VUTRB246) or GUID format (e.g., 1d582565-4247-e911-a2dc-005056b75310)",
                });
            }
        }
    }
    /**
     * Validate credentials against reference data
     */
    async function validateCredentials(record) {
        if (!record.credentials)
            return;
        const credentialsList = await fetchReferenceData("/api/references/credentials", "credentials");
        if (credentialsList === null) {
            record.validationErrors.push({
                rowNumber: record.rowNumber,
                fieldName: "Credentials",
                errorMessage: `Cannot validate '${record.credentials}' - reference data API unavailable`,
            });
        }
        else if (credentialsList.length > 0) {
            const validValues = credentialsList.map((c) => c.DisplayText);
            if (!validValues.includes(record.credentials)) {
                record.validationErrors.push({
                    rowNumber: record.rowNumber,
                    fieldName: "Credentials",
                    errorMessage: `'${record.credentials}' is not a valid credential. Must match system reference data.`,
                });
            }
        }
    }
    /**
     * Validate specialty against reference data
     */
    async function validateSpecialty(record) {
        if (!record.specialty)
            return;
        const specialtiesList = await fetchReferenceData("/api/references/specialties", "specialties");
        if (specialtiesList === null) {
            record.validationErrors.push({
                rowNumber: record.rowNumber,
                fieldName: "Specialty",
                errorMessage: `Cannot validate '${record.specialty}' - reference data API unavailable`,
            });
        }
        else if (specialtiesList.length > 0) {
            const validValues = specialtiesList.map((s) => s.DisplayText);
            if (!validValues.includes(record.specialty)) {
                record.validationErrors.push({
                    rowNumber: record.rowNumber,
                    fieldName: "Specialty",
                    errorMessage: `'${record.specialty}' is not a valid specialty. Must match system reference data.`,
                });
            }
        }
    }
    /**
     * Validate email availability
     */
    async function validateEmailAvailability(record) {
        if (!record.email)
            return;
        try {
            const response = await get(`/api/match?recoveryEmail=${encodeURIComponent(record.email)}`);
            const matches = response.data;
            if (matches && matches.length > 0) {
                const userInfo = matches[0].Practitioner;
                const firstName = userInfo?.FirstName || "Unknown";
                const lastName = userInfo?.LastName || "User";
                const userId = userInfo?.UserId || "Unknown";
                record.validationErrors.push({
                    rowNumber: record.rowNumber,
                    fieldName: "Email",
                    errorMessage: `Email '${record.email}' is already in use by ${firstName} ${lastName} (UserId: ${userId})`,
                });
            }
        }
        catch (error) {
            record.validationErrors.push({
                rowNumber: record.rowNumber,
                fieldName: "Email",
                errorMessage: `Error checking email availability: ${error instanceof Error ? error.message : "Unknown error"}`,
            });
        }
    }
    /**
     * Validate UPN domain against authenticators
     */
    async function validateUPNDomain(record) {
        if (!record.username)
            return;
        if (!record.username.includes("@")) {
            record.validationErrors.push({
                rowNumber: record.rowNumber,
                fieldName: "Username (UPN)",
                errorMessage: "Must be in format user@domain.com",
            });
            return;
        }
        const domain = record.username.split("@")[1];
        const authenticators = await fetchReferenceData("/api/identitymanagement/authenticators", "authenticators");
        if (authenticators === null) {
            record.validationErrors.push({
                rowNumber: record.rowNumber,
                fieldName: "Username (UPN)",
                errorMessage: `Cannot validate domain '${domain}' - authenticators API unavailable`,
            });
        }
        else if (authenticators.length > 0) {
            const validDomains = authenticators.map((a) => a.DomainName);
            if (!validDomains.includes(domain)) {
                record.validationErrors.push({
                    rowNumber: record.rowNumber,
                    fieldName: "Username (UPN)",
                    errorMessage: `Domain '${domain}' is not a valid authenticator domain. Valid domains: ${validDomains.join(", ")}`,
                });
            }
        }
    }
    /**
     * Validate pager carrier if pager number is provided
     */
    async function validatePagerCarrier(record) {
        if (record.pagerNumber && !record.pagerCarrier) {
            record.validationErrors.push({
                rowNumber: record.rowNumber,
                fieldName: "Pager Carrier",
                errorMessage: "Pager Carrier is required when Pager Number is provided",
            });
            return;
        }
        if (!record.pagerCarrier)
            return;
        const carriers = await fetchReferenceData("/api/references/carriers?deviceType=Pager", "pagerCarriers");
        if (carriers === null) {
            record.validationErrors.push({
                rowNumber: record.rowNumber,
                fieldName: "Pager Carrier",
                errorMessage: `Cannot validate '${record.pagerCarrier}' - carriers API unavailable`,
            });
        }
        else if (carriers.length > 0) {
            const validCarriers = carriers.map((c) => c.Name);
            if (!validCarriers.includes(record.pagerCarrier)) {
                record.validationErrors.push({
                    rowNumber: record.rowNumber,
                    fieldName: "Pager Carrier",
                    errorMessage: `'${record.pagerCarrier}' is not a valid pager carrier. Must match system reference data.`,
                });
            }
        }
    }
    /**
     * Look up IdentityId from V5UserId using the user search API
     */
    async function lookupIdentityIdFromV5UserId(v5UserId) {
        try {
            const response = await get(`/api/usermanagement/users?searchtext=${encodeURIComponent(v5UserId)}`);
            const users = response.data;
            if (users && users.length > 0) {
                // Find the exact match
                const exactMatch = users.find((u) => u.Id === v5UserId);
                if (exactMatch) {
                    return {
                        identityId: exactMatch.IdentityId,
                        accountName: exactMatch.AccountName,
                    };
                }
            }
            return null;
        }
        catch {
            return null;
        }
    }
    /**
     * Validate Copy User ID is a valid reference user
     */
    async function validateCopyUserId(record) {
        if (!record.copyUserId)
            return;
        // Check if it's V5UserId format (e.g., USER-8VUTRB0TB)
        const v5UserIdPattern = /^USER-[A-Z0-9]+$/;
        let identityId = null;
        if (v5UserIdPattern.test(record.copyUserId)) {
            // V5UserId format - need to look up the IdentityId
            const lookupResult = await lookupIdentityIdFromV5UserId(record.copyUserId);
            if (!lookupResult) {
                record.validationErrors.push({
                    rowNumber: record.rowNumber,
                    fieldName: "Reference V5UserId",
                    errorMessage: `User ${record.copyUserId} not found`,
                });
                return;
            }
            identityId = lookupResult.identityId;
            record.referenceUserAccountName = lookupResult.accountName;
            record.referenceUserV5Id = record.copyUserId;
        }
        else {
            // Assume it's a GUID/IdentityId format
            identityId = record.copyUserId;
        }
        // Validate using the IdentityId
        try {
            const referenceResponse = await get(`/api/users/practitionerProfile/${identityId}/ReferenceUser`);
            const userData = referenceResponse.data;
            // Fetch full practitioner profile to get AccountId and AccountName
            try {
                const profileResponse = await get(`/api/users/practitionerProfile/${identityId}`);
                const profileData = profileResponse.data;
                // Always capture AccountId from profile
                if (profileData.AccountId) {
                    record.referenceUserAccountId = profileData.AccountId;
                }
                if (profileData.AccountName && !record.referenceUserAccountName) {
                    record.referenceUserAccountName = profileData.AccountName;
                }
                if (profileData.V5UserId && !record.referenceUserV5Id) {
                    record.referenceUserV5Id = profileData.V5UserId;
                }
            }
            catch {
                // Profile fetch failed, continue without AccountId/AccountName
            }
            if (!userData.IsReferenceUser) {
                // Attempt to set as reference user
                const v5UserId = userData.V5UserId || record.referenceUserV5Id;
                if (v5UserId) {
                    try {
                        await put(`/api/users/practitionerProfile/${identityId}/ReferenceUser?IsReferenceUser=true&V5UserId=${v5UserId}`);
                    }
                    catch {
                        record.validationErrors.push({
                            rowNumber: record.rowNumber,
                            fieldName: "Reference V5UserId",
                            errorMessage: `User ${record.copyUserId} could not be set as a Reference User`,
                        });
                    }
                }
                else {
                    record.validationErrors.push({
                        rowNumber: record.rowNumber,
                        fieldName: "Reference V5UserId",
                        errorMessage: `User ${record.copyUserId} is not a Reference User and cannot be set as one`,
                    });
                }
            }
            // Note: referenceUserAccountId is set from the profile response above
        }
        catch (error) {
            // Handle specific error cases
            const errorMessage = error instanceof Error ? error.message : "Unknown error";
            if (errorMessage.includes("404")) {
                record.validationErrors.push({
                    rowNumber: record.rowNumber,
                    fieldName: "Reference V5UserId",
                    errorMessage: `User ${record.copyUserId} not found`,
                });
            }
            else {
                record.validationErrors.push({
                    rowNumber: record.rowNumber,
                    fieldName: "Reference V5UserId",
                    errorMessage: `Failed to validate: ${errorMessage}`,
                });
            }
        }
    }
    /**
     * Validate a single record (all validations)
     */
    async function validateRecord(record) {
        // Required field validation
        validateRequiredFields(record);
        // Format validations
        validateNPI(record);
        validatePhoneNumber(record, "mobileNumber", "Mobile Number");
        validatePhoneNumber(record, "pagerNumber", "Pager Number");
        validateReferenceUserIdFormat(record);
        // API-based validations
        await validateCredentials(record);
        await validateSpecialty(record);
        await validateEmailAvailability(record);
        await validateUPNDomain(record);
        await validatePagerCarrier(record);
        await validateCopyUserId(record);
    }
    /**
     * Validate all records
     */
    async function validateAllRecords(records, onProgress) {
        const allErrors = [];
        for (let i = 0; i < records.length; i++) {
            const record = records[i];
            if (onProgress) {
                onProgress(i + 1, records.length);
            }
            await validateRecord(record);
            allErrors.push(...record.validationErrors);
        }
        return allErrors;
    }
    /**
     * Clear the reference data cache
     */
    function clearCache() {
        cache.credentials = null;
        cache.specialties = null;
        cache.authenticators = null;
        cache.pagerCarriers = null;
    }
    return {
        validateRecord,
        validateAllRecords,
        clearCache,
    };
}
